import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import {
  emitOperationsSyncPendingChanged,
} from '@/lib/operations/offlineSyncEvents';
import offlinePayloadCrypto from './offlineCrypto';
import offlineDb, { type OfflineDomainRecord, type OfflineRecordSyncStatus } from './offlineDb';

const DOMAIN = 'app-shell' as const;
const LEGACY_FORMS_INDEXEDDB_NAME = 'totalaccess_forms_offline';
const LEGACY_FORMS_STORAGE_PURGE_FLAG = 'legacy_operational_forms_storage_purged_v1';
const LEGACY_FORMS_LOCALSTORAGE_PREFIXES = [
  'offline_form_queue',
  'offline_form_',
  'offline_form_draft_',
] as const;
const LEGACY_FORMS_DELETE_TIMEOUT_MS = 750;

let legacyFormsStoragePurgePromise: Promise<void> | null = null;

export type OperationalFormsStoreName = 'form_configs' | 'submission_queue' | 'draft_snapshots';

export type OfflineFormData = Record<string, unknown>;
export type OfflineFormConfig = Record<string, unknown> | unknown;
export type OfflineFormSectionResult = Record<string, unknown>;

export interface QueuedSubmission {
  id: string;
  formKey: string;
  formConfigId: number;
  workItemId?: string;
  entityType?: string;
  entityId?: string | number;
  formData: OfflineFormData;
  options?: {
    generatePdf?: boolean;
    status?: string;
    sectionResults?: OfflineFormSectionResult[];
    signatureData?: OfflineFormData;
    gpsData?: { lat: number; lng: number; accuracy: number };
  };
  queuedAt: string;
  retries: number;
  lastError?: string;
}

export interface StoredFormDraft {
  id: string;
  formKey: string;
  formConfigId: number;
  workItemId?: string;
  entityType?: string;
  entityId?: string | number;
  formData: OfflineFormData;
  savedAt: string;
}

export interface CachedFormConfig<TConfig = OfflineFormConfig> {
  formKey: string;
  config: TConfig;
  cachedAt: string;
}

export interface OperationalFormWorkItemTarget {
  id: string | number;
  item_type?: string | null;
  item_subtype?: string | null;
  parent_id?: string | number | null;
  metadata?: Record<string, unknown> | null;
  custom_fields?: Record<string, unknown> | null;
}

interface UpsertRecordOptions {
  parentId?: string;
  status?: string;
  syncStatus?: OfflineRecordSyncStatus;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

function getDb() {
  return offlineDb;
}

function toIso(value?: Date | string | null, fallback?: string): string {
  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  return fallback || new Date().toISOString();
}

function toEntityId(value: string | number | undefined | null): string {
  return String(value ?? '');
}

function resolveClientId(): number | null {
  const parsed = Number(authService.getUser()?.client_id ?? null);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function requireClientId(): number {
  const clientId = resolveClientId();
  if (!clientId) {
    throw new Error('Operational form offline storage requires an active client context');
  }
  return clientId;
}

function hasLegacyFormsStorageBeenPurged(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    return window.localStorage.getItem(LEGACY_FORMS_STORAGE_PURGE_FLAG) === '1';
  } catch {
    return false;
  }
}

function markLegacyFormsStoragePurged(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(LEGACY_FORMS_STORAGE_PURGE_FLAG, '1');
  } catch {
    // ignore localStorage write failures
  }
}

function purgeLegacyFormsLocalStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const keysToDelete: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key) {
        continue;
      }

      const shouldDelete = LEGACY_FORMS_LOCALSTORAGE_PREFIXES.some((prefix) => (
        key === prefix || key.startsWith(prefix)
      ));

      if (shouldDelete) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => {
      window.localStorage.removeItem(key);
    });
  } catch {
    // ignore localStorage read/remove failures
  }
}

async function deleteLegacyFormsIndexedDb(): Promise<void> {
  if (typeof indexedDB === 'undefined') {
    return;
  }

  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolve();
    };

    try {
      const request = indexedDB.deleteDatabase(LEGACY_FORMS_INDEXEDDB_NAME);
      const timeoutHandle = window.setTimeout(finish, LEGACY_FORMS_DELETE_TIMEOUT_MS);

      request.onsuccess = () => {
        window.clearTimeout(timeoutHandle);
        finish();
      };

      request.onerror = () => {
        window.clearTimeout(timeoutHandle);
        finish();
      };

      request.onblocked = () => {
        // Another tab may still hold the old database open. Do not block app startup.
      };
    } catch {
      finish();
    }
  });
}

async function ensureLegacyFormsStoragePurged(): Promise<void> {
  if (typeof window === 'undefined' || hasLegacyFormsStorageBeenPurged()) {
    return;
  }

  if (!legacyFormsStoragePurgePromise) {
    legacyFormsStoragePurgePromise = (async () => {
      purgeLegacyFormsLocalStorage();
      await deleteLegacyFormsIndexedDb();
      markLegacyFormsStoragePurged();
    })();
  }

  await legacyFormsStoragePurgePromise;
}

async function resolvePreparedClientId(): Promise<number | null> {
  await ensureLegacyFormsStoragePurged();
  return resolveClientId();
}

async function requirePreparedClientId(): Promise<number> {
  await ensureLegacyFormsStoragePurged();
  return requireClientId();
}

function extractFormKey(form: unknown): string | null {
  if (!form || typeof form !== 'object') {
    return null;
  }

  const value = (form as Record<string, unknown>).form_key;
  return typeof value === 'string' && value.trim() ? value : null;
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }

  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function buildRecordRow<T>(
  store: OperationalFormsStoreName,
  clientId: number,
  entityId: string,
  item: T,
  existing: OfflineDomainRecord | undefined,
  options?: UpsertRecordOptions,
): Promise<OfflineDomainRecord> {
  const now = toIso(options?.updatedAt);
  const payload = await offlinePayloadCrypto.encryptValue(item);

  return {
    id: existing?.id,
    domain: DOMAIN,
    store,
    client_id: clientId,
    entity_id: entityId,
    parent_id: options?.parentId,
    status: options?.status,
    sync_status: options?.syncStatus || existing?.sync_status || 'synced',
    created_at: existing?.created_at || toIso(options?.createdAt, now),
    updated_at: now,
    payload,
    payload_size: payload.length,
  };
}

async function getStoreRows(store: OperationalFormsStoreName, clientId: number): Promise<OfflineDomainRecord[]> {
  const db = getDb();
  if (!db) {
    return [];
  }

  return db.records
    .where('[domain+store+client_id]')
    .equals([DOMAIN, store, clientId])
    .toArray();
}

async function getStoreRow(store: OperationalFormsStoreName, clientId: number, entityId: string): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }

  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, store, clientId, entityId])
    .first();
}

const operationalFormsStore = {
  async upsertRecord<T>(
    store: OperationalFormsStoreName,
    clientId: number,
    entityId: string | number,
    item: T,
    options?: UpsertRecordOptions,
  ): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const normalizedEntityId = toEntityId(entityId);
    const existing = await getStoreRow(store, clientId, normalizedEntityId);
    const row = await buildRecordRow(store, clientId, normalizedEntityId, item, existing, options);
    await db.records.put(row);
  },

  async getRecord<T>(store: OperationalFormsStoreName, clientId: number, entityId: string | number): Promise<T | undefined> {
    const row = await getStoreRow(store, clientId, toEntityId(entityId));
    return decryptRecordPayload<T>(row);
  },

  async listRecords<T>(store: OperationalFormsStoreName, clientId: number): Promise<T[]> {
    const rows = await getStoreRows(store, clientId);
    const values = await Promise.all(rows.map((row) => decryptRecordPayload<T>(row)));
    return values.reduce<T[]>((accumulator, value) => {
      if (value !== undefined) {
        accumulator.push(value);
      }
      return accumulator;
    }, []);
  },

  async deleteRecord(store: OperationalFormsStoreName, clientId: number, entityId: string | number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const row = await getStoreRow(store, clientId, toEntityId(entityId));
    if (row?.id) {
      await db.records.delete(row.id);
    }
  },

  async clearStore(store: OperationalFormsStoreName, clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }

    const rows = await getStoreRows(store, clientId);
    const ids = rows
      .map((row) => row.id)
      .filter((value): value is number => typeof value === 'number');

    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },

  async countRecords(store: OperationalFormsStoreName, clientId: number): Promise<number> {
    const db = getDb();
    if (!db) {
      return 0;
    }

    return db.records
      .where('[domain+store+client_id]')
      .equals([DOMAIN, store, clientId])
      .count();
  },
};

export async function getQueuedOfflineFormSubmissions(): Promise<QueuedSubmission[]> {
  const clientId = await resolvePreparedClientId();
  if (!clientId) {
    return [];
  }

  const entries = await operationalFormsStore.listRecords<QueuedSubmission>('submission_queue', clientId);
  return entries.sort((left, right) => left.queuedAt.localeCompare(right.queuedAt));
}

export async function getOfflineFormQueuePendingCount(): Promise<number> {
  const clientId = await resolvePreparedClientId();
  if (!clientId) {
    return 0;
  }

  return operationalFormsStore.countRecords('submission_queue', clientId);
}

export async function cacheOfflineFormConfig<TConfig = OfflineFormConfig>(formKey: string, config: TConfig): Promise<void> {
  const clientId = await resolvePreparedClientId();
  if (!clientId || !formKey) {
    return;
  }

  const entry: CachedFormConfig<TConfig> = {
    formKey,
    config,
    cachedAt: new Date().toISOString(),
  };

  await operationalFormsStore.upsertRecord('form_configs', clientId, formKey, entry, {
    status: 'cached',
    syncStatus: 'synced',
  });
}

export async function getCachedOfflineFormConfig<TConfig = OfflineFormConfig>(formKey: string): Promise<TConfig | null> {
  const clientId = await resolvePreparedClientId();
  if (!clientId || !formKey) {
    return null;
  }

  const entry = await operationalFormsStore.getRecord<CachedFormConfig<TConfig>>('form_configs', clientId, formKey);
  return entry?.config ?? null;
}

export async function listCachedOfflineFormConfigs<TConfig = OfflineFormConfig>(): Promise<Array<CachedFormConfig<TConfig>>> {
  const clientId = await resolvePreparedClientId();
  if (!clientId) {
    return [];
  }

  return operationalFormsStore.listRecords<CachedFormConfig<TConfig>>('form_configs', clientId);
}

function buildQueuedSubmissionId(sub: Omit<QueuedSubmission, 'id' | 'queuedAt' | 'retries'>): string {
  const targetScope = sub.workItemId ?? sub.entityId ?? sub.entityType;
  if (targetScope != null && String(targetScope).trim()) {
    return `queued-form-submission:${sub.formKey}:${String(targetScope)}`;
  }

  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export async function queueOfflineFormSubmission(
  sub: Omit<QueuedSubmission, 'id' | 'queuedAt' | 'retries'>,
): Promise<QueuedSubmission> {
  const clientId = await requirePreparedClientId();
  const entry: QueuedSubmission = {
    ...sub,
    id: buildQueuedSubmissionId(sub),
    queuedAt: new Date().toISOString(),
    retries: 0,
  };

  await operationalFormsStore.upsertRecord('submission_queue', clientId, entry.id, entry, {
    parentId: entry.workItemId || entry.formKey,
    status: 'pending',
    syncStatus: 'pending',
  });
  emitOperationsSyncPendingChanged('forms');
  return entry;
}

export async function clearQueuedOfflineFormSubmission(submissionId: string): Promise<void> {
  const clientId = await resolvePreparedClientId();
  if (!clientId || !submissionId) {
    return;
  }

  await operationalFormsStore.deleteRecord('submission_queue', clientId, submissionId);
  emitOperationsSyncPendingChanged('forms');
}

export async function saveOfflineFormDraft(
  draft: Omit<StoredFormDraft, 'savedAt'>,
): Promise<StoredFormDraft> {
  const clientId = await requirePreparedClientId();
  const entry: StoredFormDraft = {
    ...draft,
    savedAt: new Date().toISOString(),
  };

  await operationalFormsStore.upsertRecord('draft_snapshots', clientId, entry.id, entry, {
    parentId: entry.workItemId || entry.formKey,
    status: 'saved',
    syncStatus: 'synced',
  });

  return entry;
}

export async function getOfflineFormDraft(draftId: string): Promise<StoredFormDraft | null> {
  const clientId = await resolvePreparedClientId();
  if (!clientId || !draftId) {
    return null;
  }

  return (await operationalFormsStore.getRecord<StoredFormDraft>('draft_snapshots', clientId, draftId)) || null;
}

export async function clearOfflineFormDraft(draftId: string): Promise<void> {
  const clientId = await resolvePreparedClientId();
  if (!clientId || !draftId) {
    return;
  }

  await operationalFormsStore.deleteRecord('draft_snapshots', clientId, draftId);
}

export async function clearOfflineFormQueue(): Promise<void> {
  const clientId = await resolvePreparedClientId();
  if (!clientId) {
    return;
  }

  await operationalFormsStore.clearStore('submission_queue', clientId);
  emitOperationsSyncPendingChanged('forms');
}

function buildOperationalFormParams(target: OperationalFormWorkItemTarget): URLSearchParams {
  const params = new URLSearchParams({
    item_type: target.item_type || 'job',
    client_only: 'true',
  });

  const subtype = target.item_subtype
    || target.metadata?.subtype
    || target.metadata?.item_subtype
    || target.custom_fields?.subtype;

  if (subtype) {
    params.set('subtype', String(subtype));
    params.set('job_type', String(subtype));
  }

  if (target.id != null) {
    params.set('work_item_id', String(target.id));
  }

  return params;
}

export async function predownloadOperationalFormConfigs(options: {
  accessToken?: string | null;
  workItems: OperationalFormWorkItemTarget[];
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{ cached: number; failed: number; cachedFormKeys: string[] }> {
  const { workItems, onProgress } = options;
  const token = options.accessToken || authService.getTokens()?.access_token;
  const clientId = await resolvePreparedClientId();

  if (!clientId || !token || !Array.isArray(workItems) || workItems.length === 0) {
    return { cached: 0, failed: 0, cachedFormKeys: [] };
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const cachedForms = new Map<string, OfflineFormConfig>();
  let failed = 0;

  for (let index = 0; index < workItems.length; index += 1) {
    const target = workItems[index];
    const label = `${target.item_type || 'work item'} ${target.id}`;
    onProgress?.({
      current: index + 1,
      total: workItems.length,
      message: `Downloading forms for ${label}...`,
    });

    try {
      const directRes = await fetch(`${getApiUrl('/api/v1/forms/operational')}?${buildOperationalFormParams(target).toString()}`, {
        headers,
      });

      if (directRes.ok) {
        const directData = await directRes.json();
        const forms = Array.isArray(directData?.data) ? directData.data : [];
        forms.forEach((form: unknown) => {
          const formKey = extractFormKey(form);
          if (formKey) {
            cachedForms.set(formKey, form);
          }
        });
      } else {
        failed += 1;
      }

      const shouldFetchInherited = target.item_type === 'appointment' && (target.parent_id != null);
      if (shouldFetchInherited) {
        const inheritedParams = new URLSearchParams({
          parent_job_id: String(target.parent_id),
          appointment_id: String(target.id),
        });
        const inheritedRes = await fetch(`${getApiUrl('/api/v1/work-items/forms/inherited')}?${inheritedParams.toString()}`, {
          headers,
        });

        if (inheritedRes.ok) {
          const inheritedData = await inheritedRes.json();
          const inheritedForms = Array.isArray(inheritedData?.data?.inherited_forms)
            ? inheritedData.data.inherited_forms
            : [];
          inheritedForms.forEach((form: unknown) => {
            const formKey = extractFormKey(form);
            if (formKey) {
              cachedForms.set(formKey, form);
            }
          });
        } else {
          failed += 1;
        }
      }
    } catch {
      failed += 1;
    }
  }

  for (const [formKey, formConfig] of cachedForms.entries()) {
    await cacheOfflineFormConfig(formKey, formConfig);
  }

  return {
    cached: cachedForms.size,
    failed,
    cachedFormKeys: Array.from(cachedForms.keys()),
  };
}

export async function syncOfflineFormQueueNow(options?: {
  accessToken?: string | null;
}): Promise<{ synced: number; failed: number; remaining: number }> {
  const clientId = await resolvePreparedClientId();
  const token = options?.accessToken || authService.getTokens()?.access_token;

  if (!clientId) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const items = await getQueuedOfflineFormSubmissions();
  if (items.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  if (!token) {
    for (const item of items) {
      await operationalFormsStore.upsertRecord('submission_queue', clientId, item.id, {
        ...item,
        retries: item.retries + 1,
        lastError: 'Missing auth token',
      }, {
        parentId: item.workItemId || item.formKey,
        status: 'pending',
        syncStatus: 'failed',
      });
    }

    const remaining = await getOfflineFormQueuePendingCount();
    if (typeof window !== 'undefined' && items.length > 0) {
      window.dispatchEvent(new CustomEvent('forms-offline-sync-complete', {
        detail: { synced: 0, failed: items.length, remaining },
      }));
    }

    emitOperationsSyncPendingChanged('forms');
    return { synced: 0, failed: items.length, remaining };
  }

  let synced = 0;
  let failed = 0;
  const submissionsEndpoint = getApiUrl('/api/v1/forms/submissions');

  for (const item of items) {
    try {
      const res = await fetch(submissionsEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          form_configuration_id: item.formConfigId,
          form_key: item.formKey,
          work_item_id: item.workItemId,
          entity_type: item.entityType,
          entity_id: item.entityId,
          form_data: item.formData,
          overall_status: item.options?.status || 'submitted',
          section_results: item.options?.sectionResults,
          generate_pdf: item.options?.generatePdf,
          signature_data: item.options?.signatureData,
          gps_data: item.options?.gpsData,
          offline_queued_at: item.queuedAt,
        }),
      });

      if (res.ok) {
        await operationalFormsStore.deleteRecord('submission_queue', clientId, item.id);
        synced += 1;
        continue;
      }

      failed += 1;
      await operationalFormsStore.upsertRecord('submission_queue', clientId, item.id, {
        ...item,
        retries: item.retries + 1,
        lastError: `HTTP ${res.status}`,
      }, {
        parentId: item.workItemId || item.formKey,
        status: 'pending',
        syncStatus: 'failed',
      });
    } catch (error) {
      failed += 1;
      await operationalFormsStore.upsertRecord('submission_queue', clientId, item.id, {
        ...item,
        retries: item.retries + 1,
        lastError: error instanceof Error ? error.message : 'Network error',
      }, {
        parentId: item.workItemId || item.formKey,
        status: 'pending',
        syncStatus: 'failed',
      });
    }
  }

  const remaining = await getOfflineFormQueuePendingCount();
  if (typeof window !== 'undefined' && (synced > 0 || failed > 0)) {
    window.dispatchEvent(new CustomEvent('forms-offline-sync-complete', {
      detail: { synced, failed, remaining },
    }));
  }

  emitOperationsSyncPendingChanged('forms');
  return { synced, failed, remaining };
}

export default operationalFormsStore;
